// filepath: /Users/keerthivelagala/Desktop/AzureRepo/pbna-wse-lighthouse-frontend/src/screens/StandUp/hooks/useLighthouseExecution.ts
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelectedLocation } from '../../../hooks/useSelectedLocation';
import { createApiClient, apiClient } from '../../../api/apiClient';
import { handleStandUpErrorWithFriendlyMessage } from '../../../store/utils/standUpErrorHandler';
import { STANDUP_API_NAMES } from '../../../store/slices/standUpSlice';

// Metric keys in the order they should appear
const METRIC_KEYS = ['pulseChecks', 'observations', 'shiftHandovers', 'sparcWalks', 'leadershipWalks'];
const METRIC_TITLES: { [key: string]: string } = {
  'pulseChecks': 'Pulse Check',
  'observations': 'Observation',
  'shiftHandovers': 'Shift Handover',
  'sparcWalks': 'SPARC Walk',
  'leadershipWalks': 'Leadership Walk',
};

/**
 * Helper function to extract question_id from kpi_id
 * kpi_id format: "{location_id}-{year}-{period}-{week}-{question_id}"
 * Example: "2615-2026-2-4-2168" -> 2168
 */
const extractQuestionIdFromKpiId = (kpiId: string): number | undefined => {
  if (!kpiId) return undefined;
  const parts = kpiId.split('-');
  const questionId = parts[parts.length - 1];
  return questionId ? parseInt(questionId, 10) : undefined;
};

/**
 * Custom hook to manage lighthouse execution data
 * Fetches data only from lh-metric-trend API which provides both summary and chart data
 * Returns transformed data ready for consumption by components
 */
export const useLighthouseExecution = () => {
  // Get selected location from Redux
  const selectedLocation = useSelectedLocation();

  // State for metric trend data (chart and summary data from API)
  const [metricTrendData, setMetricTrendData] = useState<any>(null);
  const [metricTrendLoading, setMetricTrendLoading] = useState(false);
  const [metricTrendError, setMetricTrendError] = useState<string | null>(null);
  const [lastFetchedTrendLocation, setLastFetchedTrendLocation] = useState<string | null>(null);

  // Fetch metric trend data when location changes
  const fetchMetricTrend = useCallback(async () => {
    if (!selectedLocation?.displayText) {
      return;
    }

    // Don't refetch if already fetched for this location
    if (lastFetchedTrendLocation === selectedLocation.displayText && metricTrendData) {
      return;
    }

    const endpoint = `/stand-up/lh-metric-trend?location_name=${encodeURIComponent(selectedLocation.displayText)}&metric=all`;
    
    console.log('useLighthouseExecution: Fetching metric trend data', { location: selectedLocation.displayText, endpoint });

    setMetricTrendLoading(true);
    setMetricTrendError(null);
    try {
      const api = createApiClient();
      const response = await apiClient(
        api,
        endpoint,
        'GET'
      );
      console.log('useLighthouseExecution: Metric trend response received', response);

      // Handle both response formats
      // Format 1: { success: true, data: { data: { metrics: { pulseChecks: {...} } } } }
      // Format 2: { success: true, data: { metrics: { pulseChecks: {...} } } }
      const metricsObj = response?.data?.data?.metrics || response?.data?.metrics;
      
      if (metricsObj) {
        console.log('useLighthouseExecution: Setting metric trend data', metricsObj);
        setMetricTrendData(metricsObj);
        setLastFetchedTrendLocation(selectedLocation.displayText);
      } else {
        console.log('useLighthouseExecution: No metric trend data in response');
        setMetricTrendError('No metric trend data available');
      }
    } catch (err: any) {
      console.error('useLighthouseExecution: Error fetching metric trend:', err);
      setMetricTrendError(err?.message || 'Failed to load metric trend data');
      
      // Use centralized error handler to show overlay/modal for API failures
      try {
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.FETCH_METRIC_TREND,
          err,
          true, // displayModal = true → show overlay
          { location: selectedLocation?.displayText, endpoint }
        );
      } catch (handlerErr) {
        console.error('Error while invoking standup error handler for metric trend:', handlerErr);
      }
    } finally {
      setMetricTrendLoading(false);
    }
  }, [selectedLocation?.displayText, lastFetchedTrendLocation, metricTrendData]);

  // Fetch metric trend data when location changes
  useEffect(() => {
    if (selectedLocation?.displayText) {
      // Always fetch if location is set and we haven't fetched for this location yet
      if (!lastFetchedTrendLocation || lastFetchedTrendLocation !== selectedLocation.displayText) {
        fetchMetricTrend();
      }
    }
  }, [selectedLocation?.displayText, lastFetchedTrendLocation, fetchMetricTrend]);

  // Transform metric trend data to card format (using only lh-metric-trend API)
  const lighthouseCardsData = useMemo(() => {
    if (!metricTrendData) {
      return [];
    }

    // Helper function to format date for chart labels
    const formatChartDate = (dateString: string): string => {
      const date = new Date(dateString);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}/${day}`;
    };

    // Helper to get chart data from metric trend API response
    const getChartDataFromTrend = (metricKey: string): Array<{ date: string; value: number }> => {
      const metricData = metricTrendData[metricKey];
      
      if (metricData?.dailyData && Array.isArray(metricData.dailyData)) {
        // Map and sort the daily data
        const mapped = metricData.dailyData.map((item: any) => {
          const period = item.period !== undefined && item.period !== null ? String(item.period) : undefined;
          const week = item.week !== undefined && item.week !== null ? String(item.week) : undefined;
          const dateLabel = (period && week) ? `P${period}W${week}` : formatChartDate(item.date);
          const ts = Number(Date.parse(item.date)) || 0;
          return { date: dateLabel, value: item.value, _ts: ts };
        });

        // Sort ascending by timestamp (oldest -> newest)
        mapped.sort((a: any, b: any) => a._ts - b._ts);

        // Remove internal timestamp before returning
        return mapped.map(({ _ts, ...rest }: { _ts: number; date: string; value: number }) => rest);
      }

      return [];
    };

    // Helper to get summary data from metric trend API response
    const getSummaryFromTrend = (metricKey: string) => {
      const metricData = metricTrendData[metricKey];
      return metricData?.summary || null;
    };

    const cards: Array<{
      id: string;
      title: string;
      question_id?: number;
      is_parent?: boolean;
      cw: number;
      lcw: number;
      lcp: number;
      ytd: number;
      chartData: Array<{ date: string; value: number }>;
    }> = [];

    // Build cards from metric trend data only
    for (const metricKey of METRIC_KEYS) {
      const metricData = metricTrendData[metricKey];
      if (metricData) {
        const summary = getSummaryFromTrend(metricKey);
        const title = METRIC_TITLES[metricKey];
        
        cards.push({
          id: `lighthouse-${metricKey}`,
          title,
          question_id: extractQuestionIdFromKpiId(metricData.kpi_id),
          is_parent: false,
          cw: summary?.actual ?? 0,
          lcw: summary?.lastWeek ?? 0,
          lcp: summary?.periodToDate ?? 0,
          ytd: summary?.yearToDate ?? 0,
          chartData: getChartDataFromTrend(metricKey),
        });
      }
    }

    return cards;
  }, [metricTrendData]);

  return {
    lighthouseCardsData,
    loading: metricTrendLoading,
    error: metricTrendError,
    isDataAvailable: lighthouseCardsData.length > 0,
    // Expose raw metric trend data for overlay to use without making another API call
    metricTrendData,
  };
};
